import { type RefObject, useEffect, useRef } from "react";

/**
 * Takes a question asked from outside the chat. By default it is left in the
 * composer, focused, for the user to finish (payroll's cell questions). With
 * `send`, it is sent as it arrives: into the open conversation, or as a new
 * one. While a reply is still coming it waits in the composer instead.
 * `onPrepared` lets the caller clear the question once taken.
 */
export const usePendingQuestion = ({
  question,
  send,
  onPrepared,
  setInput,
  textareaRef,
  conversation,
}: {
  question: string | null | undefined;
  send: boolean;
  onPrepared?: () => void;
  setInput: (value: string) => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  conversation: {
    isBusy: boolean;
    isOpen: boolean;
    handleSend: (text: string) => void;
    handleStartNewChatWithMessage: (text: string) => void;
  };
}) => {
  // Read as it is now, so only a new question runs the effect.
  const current = useRef(conversation);
  current.current = conversation;
  useEffect(() => {
    if (!question) return;
    const { isBusy, isOpen, handleSend, handleStartNewChatWithMessage } =
      current.current;
    if (!send || isBusy) {
      setInput(question);
      textareaRef.current?.focus();
    } else if (isOpen) {
      handleSend(question);
    } else {
      handleStartNewChatWithMessage(question);
    }
    onPrepared?.();
  }, [question, send, onPrepared, setInput, textareaRef]);
};
